import {
  applyHandoffStorage,
  BETA_HANDOFF_PARAM,
  BETA_HANDOFF_READY,
  BETA_HANDOFF_SESSION,
  markBetaHandoffAuthMeRefreshPending,
  resolveClassicOpenerOrigin,
  writeAllowedHandoffEntries,
} from './beta-session-handoff.util';
import { consumeUiSessionHandoffCookie } from './ui-session-handoff-cookie.util';

/**
 * Runs when classic UI navigates here with ?betaHandoff=1 (same tab or opener tab).
 */
export function acceptBetaSessionFromOpener(): Promise<void> {
  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.get(BETA_HANDOFF_PARAM) !== '1') {
    return Promise.resolve();
  }

  currentUrl.searchParams.delete(BETA_HANDOFF_PARAM);
  const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
  history.replaceState(null, '', nextUrl);

  const cookiePayload = consumeUiSessionHandoffCookie();
  if (cookiePayload?.storage?.token) {
    applyHandoffStorage(cookiePayload.storage);
    markBetaHandoffAuthMeRefreshPending();
    return Promise.resolve();
  }

  if (!window.opener) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(finish, 2000);

    function finish(): void {
      window.clearTimeout(timeoutId);
      window.removeEventListener('message', onMessage);
      resolve();
    }

    function onMessage(event: MessageEvent): void {
      if (event.source !== window.opener || !isAllowedClassicOrigin(event.origin)) {
        return;
      }

      const data = event.data;
      if (!data || data.source !== 'alertops' || data.type !== BETA_HANDOFF_SESSION) {
        return;
      }

      const token =
        typeof data.token === 'string' && data.token
          ? data.token
          : typeof data.storage?.token === 'string'
            ? data.storage.token
            : '';
      if (!token) {
        finish();
        return;
      }

      applyLegacyFields(data);
      if (data.storage && typeof data.storage === 'object') {
        applyHandoffStorage(data.storage as Record<string, string>);
      }

      markBetaHandoffAuthMeRefreshPending();
      finish();
    }

    window.addEventListener('message', onMessage);
    window.opener.postMessage(
      { source: 'alertops', type: BETA_HANDOFF_READY },
      resolveClassicOpenerOrigin(),
    );
  });
}

function applyLegacyFields(data: Record<string, unknown>): void {
  const storage: Record<string, string> = {};
  if (typeof data.token === 'string' && data.token) {
    storage['token'] = data.token;
  }
  if (typeof data.refreshToken === 'string' && data.refreshToken) {
    storage['refresh_token'] = data.refreshToken;
  }
  if (typeof data.auth === 'string' && data.auth) {
    storage['token-key'] = data.auth;
  }
  if (typeof data.username === 'string' && data.username) {
    storage['username'] = data.username;
  }
  if (typeof data.userProfile === 'string' && data.userProfile && data.userProfile !== 'null') {
    storage['userLocalStorageToken'] = data.userProfile;
  }
  if (typeof data.account === 'string' && data.account.trim()) {
    storage['subdomain'] = data.account.trim().toLowerCase();
  }
  writeAllowedHandoffEntries(storage);
}

function isAllowedClassicOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    const host = url.hostname.toLowerCase();
    const alertOpsHost = host === 'alertops.com' || host.endsWith('.alertops.com');
    const localHost = host === 'localhost' || host === 'app.localhost' || host.endsWith('.localhost');
    if (url.protocol === 'https:' && alertOpsHost) {
      return true;
    }
    return (url.protocol === 'http:' || url.protocol === 'https:') && localHost;
  } catch {
    return false;
  }
}
